/**
 * Number, currency, and percent formatting utilities.
 *
 * SSOT for all numeric display formatting in the frontend.
 * Do not create local formatting functions — use these instead.
 *
 * Inspired by trade01's lib/utils/formatting.ts and follows the same
 * discipline: every numeric on screen flows through one of these.
 */

/** Format a number as USD currency. Null/undefined → "—". */
export function formatCurrency(value: number | undefined | null, decimals = 2): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/** Whole-dollar currency. Convenience wrapper. */
export function formatCurrencyWhole(value: number | undefined | null): string {
  return formatCurrency(value, 0)
}

/** Compact currency: $1.2M, $4.5K, etc. */
export function formatCurrencyCompact(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

/** Plain number with locale separators. Null → "—". */
export function formatNumber(value: number | undefined | null, decimals = 2): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Percent formatter. Input is a raw percentage (75 → "75.00%"),
 * not a fraction (0.75).
 */
export function formatPercent(value: number | undefined | null, decimals = 2): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100)
}

/** Signed number with +/- prefix. Useful for P&L deltas. */
export function formatSigned(value: number | undefined | null, decimals = 2): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—'
  const sign = value >= 0 ? '+' : ''
  return sign + formatNumber(value, decimals)
}

/** Signed currency with +/- prefix. */
export function formatSignedCurrency(value: number | undefined | null, decimals = 2): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—'
  const sign = value >= 0 ? '+' : '−'
  return sign + formatCurrency(Math.abs(value), decimals)
}

/** Format a quantity with explicit sign. -10 → "-10", +10 → "+10". */
export function formatSignedInt(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—'
  return value >= 0 ? `+${Math.trunc(value)}` : `${Math.trunc(value)}`
}

// Alias for trading UI — two-decimal currency.
export function formatCurrencyPrecise(value: number | undefined | null): string {
  return formatCurrency(value, 2)
}

